import * as _ from 'lodash-es';
import { KeyValue } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LibraryPage, ReportConfig } from '@modules/report-generator/interfaces';
import { TextElement } from '@modules/report-generator/interfaces/elements';
import { PageTitleConfiguration, TextLanguage } from '@modules/report-generator/types';
import { DateFormatPipe } from '@shared/pipes/date-format.pipe';
import { EditTextDialogComponent } from '../../shared/edit-text-dialog/edit-text-dialog.component';
import { MatDialogRef } from '@angular/material/dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';
import { createState } from '@shared/helpers/create-state.helper';
import { AppConstants } from '@core/constants';
import { TYPICAL_ERROR_ENUM } from '@shared/enums/typical-error.enum';
import { TrainingZonesComponentService } from '@core/services/apc/training-zones-component.service';
import { EditPageTitleDialogComponent } from '../../shared/edit-page-title-dialog/edit-page-title-dialog.component';
import { EditPageTitleDialogData } from '@shared/interfaces/edit-page-title-dialog.interface';
import { TestTypeMapping, UniquePageKey } from '@modules/report-generator/shared';
import { TzbDataRow } from '@shared/interfaces/edit-tzb-dialog.interface';
import { EditTzbDialogComponent } from '../../shared/edit-tzb-dialog/edit-tzb-dialog.component';
import { EditTestInfoDialogComponent } from '../../shared/edit-test-info-dialog/edit-test-info-dialog.component';
import { TestService } from '@core/services/test/test.service';
import { EditTestInfoCoverData } from '@shared/interfaces/edit-test-info-cover-dialog';

const DefaultPageTitleConfiguration: PageTitleConfiguration = {
  date: true,
  sport: true,
  athlete: true,
  coach: true,
  testDisplayId: false,
  testType: false,
};

const DefaultTestInfoCoverConfiguration: PageTitleConfiguration = {
  date: true,
  sport: true,
  athlete: true,
  coach: true,
  testDisplayId: false,
  testType: false,
};

@Component({
  selector: 'app-report-text',
  templateUrl: './report-text.component.html',
  styleUrls: ['./report-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportTextComponent implements OnInit, OnChanges {
  private readonly store = inject(ReportGeneratorStore);
  private readonly trainingZonesService = inject(TrainingZonesComponentService);
  private readonly testService = inject(TestService);

  @Input() element!: TextElement;
  @Input() reportConfig!: ReportConfig;
  @Input() page!: LibraryPage;

  // For PDF rendering specifically
  @Input() renderForPdf = false;

  typicalErrorList = createState<Array<any>>([]);
  measuredValues = createState<Array<any>>([]);
  rawTestDataTable = createState<any>({});
  calculatedDataTable = createState<any>({});
  trainingZoneJsonData = createState<Array<any>>([]);
  trainingZoneTableData = createState<Array<any>>([]);
  isSpeed = createState<boolean | null>(null);

  public zoneTableValueType = AppConstants.ZONE_TABLE_VALUE_TYPE;

  public TYPICAL_ERROR_ENUM = TYPICAL_ERROR_ENUM;

  constructor(
    private readonly datePipe: DateFormatPipe,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.renderForPdf) {
      if (this.element.type === 'raw-test-data') {
        this.setMeasuredValues();
      }
      if (this.element.type === 'traffic-lights') {
        this.setTrafficLights();
      }
      if (['lactate-measured', 'lactate-calculated'].includes(this.element.type)) {
        this.setLactateTableValues();
      }
      if (this.element.type === 'training-zones') {
        this.setTrainingZoneData();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportConfig']) {
      if (this.element.type === 'raw-test-data') {
        this.setMeasuredValues();
      }
      if (this.element.type === 'traffic-lights') {
        this.setTrafficLights();
      }
      if (['lactate-measured', 'lactate-calculated'].includes(this.element.type)) {
        this.setLactateTableValues();
      }
      if (this.element.type === 'training-zones') {
        this.setTrainingZoneData();
      }
    }
  }

  getTextContent(): string {
    const language = this.getLanguage();
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    const contentOverride: string = templateOverrides?.[UniquePageKey(this.page)]?.[this.element.slug]?.content;
    return contentOverride ?? this.element.content?.[language] ?? this.element.content?.en ?? '';
  }

  getTextHeading(): string {
    const language = this.getLanguage();
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    const contentOverride: string | undefined =
      templateOverrides?.[UniquePageKey(this.page)]?.[this.element.slug]?.heading;
    return contentOverride ?? this.element.heading?.[language] ?? this.element.heading?.en ?? '';
  }

  getTzbZones(): Array<Record<string, any>> {
    const selectedRowValues = this.getTzbSelectedRowValues();
    if (selectedRowValues) {
      return this.trainingZoneTableData.value.filter((row) => selectedRowValues.includes(row.code.value));
    }
    return this.trainingZoneTableData.value;
  }

  getTzbSelectedRowValues(): string[] | null | undefined {
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    if (!templateOverrides) {
      return undefined;
    }

    const customOverride: Record<number, string[]> =
      templateOverrides?.[UniquePageKey(this.page)]?.[this.element.slug]?.custom;
    if (!customOverride) {
      return undefined;
    }

    return customOverride[this.reportConfig.test?.training_zone_template ?? -1];
  }

  getSafeHtml(htmlString: string): SafeHtml {
    // WARNING!! Unsafe
    return this.sanitizer.bypassSecurityTrustHtml(htmlString);
  }

  getTextHeadingColor(): string {
    return this.reportConfig.template?.theme?.colors?.primary || 'rgba(34, 34, 34)';
  }

  getPageTitleColor(): string {
    const theme = this.reportConfig.template?.theme;
    return theme?.overrides?.pageTitle?.color || theme?.colors?.primary || 'rgba(34, 34, 34)';
  }

  getOrgInfo(): string {
    const org = this.reportConfig.organization;
    if (!org) {
      return '';
    }

    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    const contentOverride: string | undefined =
      templateOverrides?.[UniquePageKey(this.page)]?.[this.element.slug]?.content;
    if (contentOverride != null && contentOverride !== '') {
      return contentOverride;
    }

    const orgInfoArray = [];

    orgInfoArray.push(`<strong>${org.name}</strong>`);

    const addressText = [
      [org.invoice_address_street_number, org.invoice_address_street_name].join(' '),
      [org.invoice_address_city, org.invoice_address_zip].join(' '),
    ].join(', ');

    orgInfoArray.push(addressText);

    let orgInfoText = orgInfoArray.join(' | ');
    orgInfoText += `<br />${org.email}`;
    if (org.website) {
      orgInfoText += ` | ${org.website}`;
    }

    return orgInfoText;
  }

  getTestInfo(): Map<string, { name: string; value: string | null | undefined }> {
    const configuration = this.getTestInfoCoverConfiguration();

    console.log(configuration);

    const testInfoDetails = [];

    if (configuration.testDisplayId) {
      testInfoDetails.push(['testId', { name: 'Test ID', value: 'Loading...' }]);
    }

    if (configuration.date) {
      testInfoDetails.push(['testDate', { name: 'Date', value: '' }]);
    }

    if (configuration.sport) {
      testInfoDetails.push(['sport', { name: 'Sport', value: '' }]);
    }

    if (configuration.testType) {
      testInfoDetails.push(['testType', { name: 'Test Type', value: '' }]);
    }

    if (configuration.athlete) {
      testInfoDetails.push(['athlete', { name: 'Athlete', value: '' }]);
    }

    const testHasCoach = Boolean(this.reportConfig?.test?.coach);
    if (configuration.coach && testHasCoach) {
      testInfoDetails.push(['coach', { name: 'Coach', value: '' }]);
    }

    const testInfo: Map<string, { name: string; value: string | null | undefined }> = new Map(
      testInfoDetails as unknown as any
    );

    const test = this.reportConfig?.test;
    if (!test) {
      return testInfo;
    }

    if (configuration.testDisplayId) {
      testInfo.set('testId', { name: 'Test ID', value: test.display_id?.toString() || '' });
    }

    if (configuration.date) {
      testInfo.set('testDate', {
        name: 'Date',
        value: this.datePipe.transform(new Date(test.test_date!), 'DD-MM-YYYY'),
      });
    }

    if (configuration.sport) {
      testInfo.set('sport', { name: 'Sport', value: test.sport.name });
    }

    if (configuration.testType) {
      testInfo.set('testType', { name: 'Test Type', value: this.testService.formatTestType(test.test_type!) });
    }

    if (configuration.athlete) {
      testInfo.set('athlete', { name: 'Athlete', value: `${test.athlete?.first_name} ${test.athlete?.last_name}` });
    }

    if (configuration.coach && test.coach) {
      testInfo.set('coach', { name: 'Coach', value: `${test.coach.first_name} ${test.coach.last_name}` });
    }

    return testInfo;
  }

  getTableComposition(): Map<string, { name: string; value: string | null | undefined }> {
    const tableComposition = new Map([
      ['bodyMass', { name: 'Body Mass', value: 'Loading...' }],
      ['bodyHeight', { name: 'Body Height', value: '' }],
      ['bodyMassIndex', { name: 'Body Mass Index', value: '' }],
      ['projectedBsa', { name: 'Projected BSA', value: '' }],
      ['bodyFat', { name: 'Body Fat', value: '' }],
      ['fatFree', { name: 'Fat Free', value: '' }],
    ]);

    const metabolicProfile = this.reportConfig?.metabolicProfile;
    if (!metabolicProfile) {
      return tableComposition;
    }

    const bodyComposition = metabolicProfile[0]?.table_data;
    if (!bodyComposition) {
      return tableComposition;
    }

    // Display ID
    tableComposition.set('bodyMass', { name: 'Body Mass', value: bodyComposition.body_mass });
    tableComposition.set('bodyHeight', { name: 'Body Height', value: bodyComposition.body_height });
    tableComposition.set('bodyMassIndex', { name: 'Body Mass Index', value: bodyComposition.body_mass_index });
    tableComposition.set('projectedBsa', { name: 'Projected BSA', value: bodyComposition.projected_BSA });
    tableComposition.set('bodyFat', { name: 'Body Fat', value: bodyComposition.body_fat });
    tableComposition.set('fatFree', { name: 'Fat Free', value: bodyComposition.fat_free });

    return tableComposition;
  }

  getPageTitleConfiguration(): PageTitleConfiguration {
    return {
      ...DefaultPageTitleConfiguration,
      ...(this.reportConfig.template?.theme?.overrides?.pageTitle ?? {}),
      ...(this.element.configuration ?? {}),
    };
  }

  getTestInfoCoverConfiguration(): PageTitleConfiguration {
    return {
      ...DefaultTestInfoCoverConfiguration,
      ...(this.reportConfig.template?.theme?.overrides?.testInfoCover ?? {}),
      ...(this.element.configuration ?? {}),
    };
  }

  getPageHeader(): Record<string, any> {
    const configuration = this.getPageTitleConfiguration();

    const details = {
      header: '',
      color: this.getPageTitleColor(),
      date: '',
      sport: '',
      athlete: '',
      coach: '',
      testDisplayId: '',
      testType: '',
    };
    details.header = this.getTextContent() ?? 'Page Title';

    const test = this.reportConfig?.test;
    if (!test) {
      return details;
    }

    if (configuration.date) {
      details.date = this.datePipe.transform(new Date(test.test_date!), 'DD-MM-YYYY');
    }

    if (configuration.sport) {
      details.sport = test.sport.name;
    }

    if (configuration.athlete) {
      details.athlete = `${test.athlete?.first_name} ${test.athlete?.last_name}`;
    }

    if (configuration.coach && test.coach) {
      details.coach = `${test.coach.first_name} ${test.coach.last_name}`;
    }

    if (configuration.testDisplayId) {
      details.testDisplayId = test.display_id?.toString() ?? '';
    }

    if (configuration.testType) {
      details.testType = TestTypeMapping[test.test_type!];
    }

    return details;
  }

  preserveOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => {
    return 0; // Don't sort, maintain original order
  };

  getHeadingColor(): string {
    if (!['page-header', 'raw-test-data', 'lactate-measured', 'lactate-calculated'].includes(this.element.type)) {
      return 'rgba(34, 34, 34)';
    }

    const theme = this.reportConfig.template?.theme;

    if (['Cover', 'Section'].includes(this.page.section_name)) {
      return theme?.colors?.primary || 'rgba(34, 34, 34)';
    }

    // Priority order: overrides > primary > default
    if (this.element.type === 'page-header') {
      return theme?.overrides?.pageTitle?.color || theme?.colors?.primary || 'rgba(34, 34, 34)';
    } else {
      return theme?.colors?.primary || 'rgba(34, 34, 34)';
    }
  }

  getSecondaryColor(): string {
    return this.reportConfig.template?.theme?.colors?.secondary || 'rgba(34, 34, 34)';
  }

  setTrafficLights(): void {
    const metabolicProfile: Array<Record<string, any>> | undefined = this.reportConfig?.metabolicProfile;
    if (!metabolicProfile) {
      return;
    }

    const testDataWrapper = metabolicProfile[6];
    const testData =
      testDataWrapper && Array.isArray(testDataWrapper) && testDataWrapper.length > 0
        ? testDataWrapper[0]
        : testDataWrapper;
    const trafficLights = testData?.data_accuracy?.traffic_lights;
    if (trafficLights && Array.isArray(trafficLights) && trafficLights.length > 0) {
      this.typicalErrorList.set(trafficLights);
    }
  }

  onEditText(): void {
    const dialogRef: MatDialogRef<EditTextDialogComponent> = this.dialog.open(EditTextDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        heading: this.getTextHeading(),
        content: this.getTextContent(),
        canEditContent: this.element.content != null,
        canEditHeading: this.element.heading != null,
        editor: {
          width: 700,
          height: 350,
        },
      },
    });

    dialogRef.afterClosed().subscribe(({ heading, content }: { heading: string; content: string }) => {
      this.store.overrideText(UniquePageKey(this.page), this.element.slug, content, heading);
    });
  }

  onEditTZB(): void {
    if (!this.trainingZoneTableData.value) {
      return;
    }

    const selectedRowValues = this.getTzbSelectedRowValues();
    const allRows = this.trainingZoneTableData.value.map((v) => ({ name: v.name.value, value: v.code.value }));
    const selectedRows = selectedRowValues
      ? allRows.filter((row) => selectedRowValues.includes(row.value))
      : [...allRows];

    const dialogRef: MatDialogRef<EditTzbDialogComponent> = this.dialog.open(EditTzbDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        allRows,
        selectedRows,
        heading: this.getTextHeading(),
        content: this.getTextContent(),
        canEditContent: this.element.content != null,
        canEditHeading: this.element.heading != null,
        editor: {
          width: 700,
          height: 200,
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        // Dialog cancelled - do nothing
        return;
      }

      const { heading, content, selectedRows }: { heading: string; content: string; selectedRows: Array<TzbDataRow> } =
        result;
      if ((selectedRows ?? []).length && this.reportConfig.test?.training_zone_template) {
        const customOverride = {
          [this.reportConfig.test?.training_zone_template]: selectedRows.map((row) => row.value),
        };
        this.store.overrideText(UniquePageKey(this.page), this.element.slug, content, heading, customOverride);
      } else {
        this.store.overrideText(UniquePageKey(this.page), this.element.slug, content, heading);
      }
    });
  }

  onEditOrgInfo(): void {
    const dialogRef: MatDialogRef<EditTextDialogComponent> = this.dialog.open(EditTextDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        content: this.getOrgInfo(),
        canEditHeading: false,
        editor: {
          width: 700,
          height: 350,
        },
      },
    });

    dialogRef.afterClosed().subscribe(({ heading, content }: { heading: string; content: string }) => {
      this.store.overrideText(UniquePageKey(this.page), this.element.slug, content);
    });
  }

  onEditTestInfoCover(): void {
    const dialogRef: MatDialogRef<EditTestInfoDialogComponent> = this.dialog.open(EditTestInfoDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        title: this.getTextContent(),
        ...this.getTestInfoCoverConfiguration(),
      },
    });

    dialogRef.afterClosed().subscribe((configuration: EditTestInfoCoverData) => {
      const currentTheme = this.reportConfig.template?.theme;
      if (currentTheme == null) {
        return;
      }

      if (!configuration) {
        return;
      }

      const updatedTemplate = {
        ...this.reportConfig.template!,
        theme: {
          ...currentTheme,
          overrides: {
            ...(currentTheme?.overrides ?? {}),
            testInfoCover: configuration,
          },
        },
      };
      this.store.updateConfig({ template: updatedTemplate });
    });
  }

  onEditPageTitle(): void {
    const dialogRef: MatDialogRef<EditPageTitleDialogComponent> = this.dialog.open(EditPageTitleDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        title: this.getTextContent(),
        ...this.getPageTitleConfiguration(),
      },
    });

    dialogRef.afterClosed().subscribe((configuration: EditPageTitleDialogData) => {
      const currentTheme = this.reportConfig.template?.theme;
      if (currentTheme == null) {
        return;
      }

      if (!configuration) {
        return;
      }

      this.store.overrideText(UniquePageKey(this.page), this.element.slug, configuration.title);

      const updatedTemplate = {
        ...this.reportConfig.template!,
        theme: {
          ...currentTheme,
          overrides: {
            ...(currentTheme?.overrides ?? {}),
            pageTitle: configuration,
          },
        },
      };
      this.store.updateConfig({ template: updatedTemplate });
    });
  }

  getMaxLowUpTarget(): number {
    return 6;
  }

  getMaxOutputValues(trainingZoneJsonData: any): number {
    let maxOut: number = 0;
    if (trainingZoneJsonData) {
      _.forEach(trainingZoneJsonData, (zone): void => {
        const countUpper: number = Object.keys(zone.output_values).length;
        if (countUpper > maxOut) {
          maxOut = countUpper;
        }
      });
    }

    return maxOut;
  }

  getWidthOutputValue() {
    return this.trainingZonesService.getWidthOutputValue(this.trainingZoneJsonData.value);
  }

  onRowClick(event: any, zoneIndex: number, type: number, valueIndex: number): void {
    const valueText = event.target.innerText.replace(/\r?\n|\r|\s/g, '');
    const zone = this.trainingZoneJsonData.value[zoneIndex];
    const lowerKeys: string[] = Object.keys(zone.lower);
    const upperKeys: string[] = Object.keys(zone.upper);
    const targetKeys: string[] = Object.keys(zone.target);
    const additionalKeys: string[] = Object.keys(zone.output_values);
    if (type === this.zoneTableValueType.LOWER) {
      this.trainingZoneJsonData.update((v) => {
        v[zoneIndex]['lower'][lowerKeys[valueIndex]] = valueText;
        return v;
      });
    } else if (type === this.zoneTableValueType.UPPER) {
      this.trainingZoneJsonData.update((v) => {
        v[zoneIndex]['upper'][upperKeys[valueIndex]] = valueText;
        return v;
      });
    } else if (type === this.zoneTableValueType.TARGET) {
      this.trainingZoneJsonData.update((v) => {
        v[zoneIndex]['target'][targetKeys[valueIndex]] = valueText;
        return v;
      });
    } else if (type === this.zoneTableValueType.OUTPUT) {
      this.trainingZoneJsonData.update((v) => {
        v[zoneIndex]['output_values'][additionalKeys[valueIndex]] = valueText;
        return v;
      });
    }
  }

  firstRowCellStyle(zone: any): Record<string, any> {
    const { color: zoneColor, textColor } = zone;
    if (zoneColor && textColor) {
      return {
        'background-color': `${zoneColor}`,
        color: `${textColor}`,
      };
    }
    return {};
  }

  excludeFromPdf(): boolean {
    const isComment = this.element?.heading?.en === '[Comments and personal notes]';
    const textContent = this.getTextContent();
    return isComment && !textContent;
  }

  private setMeasuredValues(): void {
    if (!this.reportConfig) {
      return;
    }

    this.isSpeed.set(this.reportConfig.test?.sport?.simulation_type == AppConstants.SIMULATE_TYPE.SPEED);

    const testDataWrapper = this.reportConfig?.metabolicProfile?.[6];
    const testData =
      testDataWrapper && Array.isArray(testDataWrapper) && testDataWrapper.length > 0
        ? testDataWrapper[0]
        : testDataWrapper;

    if (!testData) {
      return;
    }

    this.measuredValues.set(testData?.cp19.measured_values ?? []);
  }

  private setLactateTableValues(): void {
    if (!this.reportConfig) {
      return;
    }

    this.isSpeed.set(this.reportConfig.test?.sport?.simulation_type == AppConstants.SIMULATE_TYPE.SPEED);

    const testDataWrapper = this.reportConfig?.metabolicProfile?.[6];
    const testData =
      testDataWrapper && Array.isArray(testDataWrapper) && testDataWrapper.length > 0
        ? testDataWrapper[0]
        : testDataWrapper;

    if (!testData) {
      return;
    }

    this.rawTestDataTable.set(JSON.parse(testData.table_data.measured_values_table));
    this.calculatedDataTable.set(JSON.parse(testData.table_data.calculated_values_table));
  }

  private setTrainingZoneData(): void {
    const tzData = this.reportConfig.trainingZones;
    if (!tzData) {
      return;
    }

    this.trainingZoneJsonData.set(this.trainingZonesService.convertZeroValueToString(tzData));
    this.trainingZoneJsonData.update((v) => {
      return v.map((item: any) => {
        item.upper_length = this.trainingZonesService.getColspan(item.upper);
        item.lower_length = this.trainingZonesService.getColspan(item.lower);
        item.target_length = this.trainingZonesService.getColspan(item.target);
        return item;
      });
    });

    this.trainingZoneTableData.set(this.trainingZonesService.convertJsonDataToTable(this.trainingZoneJsonData.value));
  }

  private getLanguage(): TextLanguage {
    return this.reportConfig.language || 'en';
  }
}
